import { Quaternion, Vector3, Web3DViewer } from '@technology/web3d';
import {
  AlignmentMarkerMovedEvent,
  AlignmentNavigationStatus,
  AlignmentNavigationStatusChangedEvent,
  AlignmentNavigationTool,
  AlignmentNavigationToolEvent,
  StartNavigationOptions,
} from '@technology/web3d-plugin-trimbim';
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  Setter,
} from 'solid-js';
import {
  TrimBimCapabilityProvider,
  TrajectoryMetadataProvider,
  Web3dTool,
  SectionChainageData,
  getTrbModelId,
  web3dChainageToChainage,
  chainageToWeb3dChainage,
  chainageToCumDistance,
  cumDistanceToChainage,
} from '@trimble/web3d-providers';
import type { AlignmentPosition } from '@trimble/web3d-providers';
import { NavigationalCapabilityBase } from './base/navigational-capability-base';
import { NavigationTool } from '../helpers/web3d-tool-helper';

export class AlignmentNavigationCapability extends NavigationalCapabilityBase {
  protected override requiredWeb3DTools: NavigationTool[] = [
    Web3dTool.AlignmentNavigation,
  ];

  /**
   * Override to prevent automatic disable when the navigated model is unloaded.
   * This allows the user to start a new navigation on a different path without
   * having to re-enable the alignment navigation capability.
   */
  protected override trackIsEnabled(): void {
    createEffect(() => {
      if (!this.provider) {
        return;
      }
      const hasModels = this.provider.hasItems();
      if (hasModels) {
        this.enable();
      }
    });
  }

  private readonly setVerticalPanOnly: Setter<boolean>;
  public readonly verticalPanOnly: Accessor<boolean>;

  private readonly setAutoPilot: Setter<boolean>;
  public readonly autoPilot: Accessor<boolean>;

  public readonly status: Accessor<AlignmentNavigationStatus>;
  private readonly setStatus: Setter<AlignmentNavigationStatus>;

  public readonly alignmentModelId: Accessor<string | undefined>;
  private readonly setAlignmentModelId: Setter<string | undefined>;

  public readonly isAlignment: Accessor<boolean | undefined>;
  private readonly setIsAlignment: Setter<boolean | undefined>;

  public readonly beginStation: Accessor<number | undefined>;
  private readonly setBeginStation: Setter<number | undefined>;

  public readonly endStation: Accessor<number | undefined>;
  private readonly setEndStation: Setter<number | undefined>;

  public readonly chainage: Accessor<number | undefined>;
  private readonly setChainage: Setter<number | undefined>;

  public readonly isInOverLength: Accessor<boolean>;
  private readonly setIsInOverLength: Setter<boolean>;

  public readonly distance: Accessor<number | undefined>;
  private readonly setDistance: Setter<number | undefined>;

  public readonly beginDistance: Accessor<number | undefined>;
  private readonly setBeginDistance: Setter<number | undefined>;

  public readonly endDistance: Accessor<number | undefined>;
  private readonly setEndDistance: Setter<number | undefined>;

  public readonly crossSectionEnabled: Accessor<boolean>;
  public readonly setCrossSectionEnabled: Setter<boolean>;

  public readonly pickingActive: Accessor<boolean>;
  public readonly setPickingActive: Setter<boolean>;

  public readonly direction: Accessor<1 | -1>;
  private readonly setDirection: Setter<1 | -1>;

  private readonly alignmentNavigationTool: AlignmentNavigationTool;

  constructor(
    viewer: Web3DViewer,
    trimBimProvider: TrimBimCapabilityProvider,
    private readonly trajectoryMetadataProvider: TrajectoryMetadataProvider
  ) {
    super(viewer, trimBimProvider);

    this.alignmentNavigationTool = this.viewer.tools.get(
      AlignmentNavigationTool
    )!;

    const [status, setStatus] = createSignal<AlignmentNavigationStatus>(
      AlignmentNavigationStatus.Inactive
    );
    this.status = status;
    this.setStatus = setStatus;

    const [activeModelId, setActiveModelId] = createSignal<string | undefined>(
      undefined
    );
    this.alignmentModelId = activeModelId;
    this.setAlignmentModelId = setActiveModelId;

    const [isAlignment, setIsAlignment] = createSignal<boolean | undefined>(
      undefined
    );
    this.isAlignment = isAlignment;
    this.setIsAlignment = setIsAlignment;

    const [beginStation, setBeginStation] = createSignal<number | undefined>(
      undefined
    );
    this.beginStation = beginStation;
    this.setBeginStation = setBeginStation;

    const [endStation, setEndStation] = createSignal<number | undefined>(
      undefined
    );
    this.endStation = endStation;
    this.setEndStation = setEndStation;

    const [chainage, setChainage] = createSignal<number | undefined>(undefined);
    this.chainage = chainage;
    this.setChainage = setChainage;

    const [isInOverLength, setIsInOverLength] = createSignal<boolean>(false);
    this.isInOverLength = isInOverLength;
    this.setIsInOverLength = setIsInOverLength;

    const [distance, setDistance] = createSignal<number | undefined>(undefined);
    this.distance = distance;
    this.setDistance = setDistance;

    const [beginDistanceSignal, setBeginDistance] = createSignal<
      number | undefined
    >(undefined);
    this.beginDistance = beginDistanceSignal;
    this.setBeginDistance = setBeginDistance;

    const [endDistanceSignal, setEndDistance] = createSignal<
      number | undefined
    >(undefined);
    this.endDistance = endDistanceSignal;
    this.setEndDistance = setEndDistance;

    const [crossSectionEnabled, setCrossSectionEnabled] = createSignal<boolean>(
      this.alignmentNavigationTool.crossSectionEnabled
    );
    this.crossSectionEnabled = crossSectionEnabled;
    this.setCrossSectionEnabled = setCrossSectionEnabled;

    const [verticalPanOnly, setVerticalPanOnly] = createSignal<boolean>(
      this.alignmentNavigationTool.verticalPanOnly
    );
    this.verticalPanOnly = verticalPanOnly;
    this.setVerticalPanOnly = setVerticalPanOnly;

    const [pickingActive, setPickingActive] = createSignal<boolean>(
      this.alignmentNavigationTool.pickingActive
    );
    this.pickingActive = pickingActive;
    this.setPickingActive = setPickingActive;

    const [autoPilot, setAutoPilot] = createSignal<boolean>(
      this.alignmentNavigationTool.autoPilot
    );
    this.autoPilot = autoPilot;
    this.setAutoPilot = setAutoPilot;

    const [direction, setDirectionSignal] = createSignal<1 | -1>(1);
    this.direction = direction;
    this.setDirection = setDirectionSignal;

    // Note: We intentionally do NOT deactivate when status becomes Inactive.
    // This allows the user to pick a new line to navigate when the current model is unloaded.

    // Derive the resolved metadata for the current alignment model.
    // Reads alignmentModelId, items(), and loadingItems (via getById) so the
    // memo automatically re-evaluates when metadata arrives after async analysis.
    const resolvedMetadata = createMemo(() => {
      const currentModelId = this.alignmentModelId();
      if (!currentModelId) return undefined;

      this.trajectoryMetadataProvider.items();

      const metadataId = getTrbModelId(currentModelId) ?? currentModelId;
      return this.trajectoryMetadataProvider.getById(metadataId);
    });

    createEffect(
      on(resolvedMetadata, (metadata) => {
        if (!metadata) return;

        this.setIsAlignment(metadata.isAlignment);

        if (metadata.isAlignment) {
          this.setBeginStation(metadata.beginStation);
          this.setEndStation(metadata.endStation);
          this.setBeginDistance(metadata.beginDistance);
          this.setEndDistance(metadata.endDistance);
        } else {
          const lineCount =
            metadata.linesPerEntity?.get(
              this.alignmentNavigationTool.alignmentEntityId!
            ) || 0;
          this.setBeginStation(0);
          this.setEndStation(lineCount);
          this.setBeginDistance(0);
          this.setEndDistance(lineCount);
        }
      })
    );

    createEffect(() => {
      this.alignmentNavigationTool.verticalPanOnly = this.verticalPanOnly();
    });

    createEffect(() => {
      this.alignmentNavigationTool.autoPilot = this.autoPilot();
    });

    createEffect(() => {
      this.alignmentNavigationTool.crossSectionEnabled =
        this.crossSectionEnabled();
    });

    createEffect(() => {
      this.alignmentNavigationTool.pickingActive = this.pickingActive();
    });
  }

  public readonly toggleAlignmentNavigationTool = async (
    crossSectionEnabled = false,
    isStartingNavigation = false
  ): Promise<void> => {
    this.viewer.addEventListener(
      AlignmentNavigationToolEvent.AlignmentNavigationStatusChanged,
      this.navigationStatusChangedHandler
    );
    this.viewer.addEventListener(
      AlignmentNavigationToolEvent.AlignmentMarkerMoved,
      this.navigationPositionChangedHandler
    );
    this.setVerticalPanOnly(this.alignmentNavigationTool.verticalPanOnly);
    this.setAutoPilot(this.alignmentNavigationTool.autoPilot);
    this.setIsAlignment(false);
    this.alignmentNavigationTool.autoPilotSpeed = 30;
    this.setCrossSectionEnabled(crossSectionEnabled);
    this.alignmentNavigationTool.crossSectionEnabled = crossSectionEnabled;
    this.enable();
    this.activate();
    // When starting navigation directly, don't enable picking mode
    // The tool's startNavigation will handle the transition to Navigating status
    if (!isStartingNavigation) {
      // Automatically enable picking mode so user can immediately click to select a line
      this.setPickingActive(true);
      this.alignmentNavigationTool.pickingActive = true;
    }
    return Promise.resolve();
  };

  /**
   * Deactivates the capability and exits navigation if active.
   * This ensures proper cleanup when another navigation capability
   * is activated via MutuallyExclusiveCapabilityCoordinator.
   */
  public override deactivate(): boolean {
    const wasDeactivated = super.deactivate();
    if (wasDeactivated) {
      void this.exitNavigation();
    }
    return wasDeactivated;
  }

  /**
   * Start navigation on a specific path/alignment model.
   * If the capability is not active, it will be activated first.
   * If already navigating on the same path, this is a no-op.
   * @param modelId The model ID of the path/alignment to navigate
   * @param section The section to start navigation from (default: 0)
   * @param web3dChainage The chainage to start navigation from (default: 0)
   */
  public async startNavigation(
    modelId: string,
    startNavigationOptions: StartNavigationOptions
  ): Promise<void> {
    // If already navigating on this path, do nothing
    if (
      this.status() === AlignmentNavigationStatus.Navigating &&
      this.alignmentModelId() === modelId
    ) {
      return;
    }

    const isCrossSection = startNavigationOptions.crossSectionEnabled ?? false;

    // Activate the tool without enabling picking mode, preserving the requested cross-section mode
    await this.toggleAlignmentNavigationTool(isCrossSection, true);

    const options: StartNavigationOptions = {
      ...startNavigationOptions,
      ...(isCrossSection && { forceLookForward: true }),
    };

    await this.alignmentNavigationTool.startNavigation(modelId, options);
  }

  public async getNavigationState(): Promise<
    StartNavigationOptions | undefined
  > {
    return this.alignmentNavigationTool.getNavigationState();
  }

  public async snapToAlignment(): Promise<void> {
    this.alignmentNavigationTool.snapToAlignment();
    return Promise.resolve();
  }

  public async exitNavigation(): Promise<void> {
    this.setCrossSectionEnabled(false);
    this.alignmentNavigationTool.crossSectionEnabled = false;
    this.alignmentNavigationTool.exitNavigation();
    return Promise.resolve();
  }

  public async toggleVerticalPanLock(): Promise<void> {
    this.setVerticalPanOnly(!this.verticalPanOnly());
    return Promise.resolve();
  }

  public async toggleAutoPilot(): Promise<void> {
    this.setAutoPilot(!this.autoPilot());
    return Promise.resolve();
  }

  public async togglePickingActive(): Promise<void> {
    this.setPickingActive(!this.pickingActive());
    return Promise.resolve();
  }

  /**
   * Returns true when the pane's {@link TrajectoryMetadataProvider} has
   * analyzed metadata for `modelId`.  Reading `items()` inside a
   * `createEffect` establishes a reactive dependency so the effect
   * re-runs when analysis completes.
   */
  public isNavigationReady(modelId: string): boolean {
    const metadataId = getTrbModelId(modelId) ?? modelId;
    this.trajectoryMetadataProvider.items();
    return this.trajectoryMetadataProvider.getById(metadataId) !== undefined;
  }

  public applyDirection(value: 1 | -1): void {
    this.setDirection(value);
    this.alignmentNavigationTool.direction = value;
  }

  public get currentAlignmentPoint(): Vector3 {
    return this.alignmentNavigationTool.currentAlignmentPoint;
  }

  public async navigateToWorldPosition(
    position: Vector3,
    quaternion?: Quaternion
  ): Promise<void> {
    if (this.status() !== AlignmentNavigationStatus.Navigating) {
      return;
    }
    await this.alignmentNavigationTool.navigateToPosition(position, quaternion);
  }

  /**
   * Accepted only while navigating, so the alignment session is snapped to the
   * target instead of being torn down by a switch to another navigation mode.
   */
  public override tryNavigateToWorldPosition(
    position: Vector3,
    quaternion?: Quaternion
  ): boolean {
    if (this.status() !== AlignmentNavigationStatus.Navigating) {
      return false;
    }
    void this.navigateToWorldPosition(position, quaternion);
    return true;
  }

  public override cleanup(): Promise<void> {
    this.viewer.removeEventListener(
      AlignmentNavigationToolEvent.AlignmentNavigationStatusChanged,
      this.navigationStatusChangedHandler
    );
    this.viewer.removeEventListener(
      AlignmentNavigationToolEvent.AlignmentMarkerMoved,
      this.navigationPositionChangedHandler
    );
    this.setVerticalPanOnly(true);
    this.setAutoPilot(false);
    this.setPickingActive(false);
    this.setCrossSectionEnabled(false);
    return Promise.resolve();
  }

  private readonly navigationStatusChangedHandler: EventListener = (
    e: Event
  ): void => {
    const {
      detail: { status, verticalPanOnly, crossSectionEnabled, pickingActive },
    } = e as AlignmentNavigationStatusChangedEvent;

    this.setStatus(status);
    this.setVerticalPanOnly(verticalPanOnly);
    this.setCrossSectionEnabled(crossSectionEnabled);
    this.setPickingActive(pickingActive);
    this.setAlignmentModelId(this.alignmentNavigationTool.alignmentModelId);
  };

  private readonly navigationPositionChangedHandler: EventListener = (
    e: Event
  ): void => {
    this.setStatus(AlignmentNavigationStatus.Navigating);
    this.setAlignmentModelId(this.alignmentNavigationTool.alignmentModelId);
    const detail = (e as AlignmentMarkerMovedEvent).detail;
    if (!detail) return;
    this.setDirection(this.alignmentNavigationTool.direction);
    const navModelId =
      getTrbModelId(this.alignmentNavigationTool.alignmentModelId) ??
      this.alignmentNavigationTool.alignmentModelId;
    const metadata = this.trajectoryMetadataProvider.getById(navModelId);
    if (!metadata) {
      // Metadata is still being analyzed — skip update.
      // The reactive effect on items/loading will update signals once ready.
      return;
    }

    if (metadata.isAlignment) {
      const entityId = this.alignmentNavigationTool.alignmentEntityId;
      const web3dChainage = this.alignmentNavigationTool.chainage || 0;
      const sectionChainage = metadata.sectionChainages?.find(
        (section: SectionChainageData) => section.entityId === entityId
      );
      const result = sectionChainage
        ? web3dChainageToChainage(web3dChainage, sectionChainage)
        : undefined;
      this.setBeginStation(metadata.beginStation);
      this.setEndStation(metadata.endStation);
      this.setChainage(result?.chainage);
      this.setIsInOverLength(result?.isInOverLength ?? false);
      this.setBeginDistance(metadata.beginDistance);
      this.setEndDistance(metadata.endDistance);

      if (result && sectionChainage) {
        const cumDist = chainageToCumDistance(result, sectionChainage);
        this.setDistance(
          cumDist !== undefined
            ? sectionChainage.beginDistance + cumDist
            : undefined
        );
      } else {
        this.setDistance(undefined);
      }
    } else {
      const lineCount =
        metadata.linesPerEntity?.get(
          this.alignmentNavigationTool.alignmentEntityId!
        ) || 0;
      const currentChainage = this.alignmentNavigationTool.chainage || 0;
      this.setBeginStation(0);
      this.setEndStation(lineCount);
      this.setChainage(currentChainage);
      this.setIsInOverLength(false);
      this.setDistance(currentChainage);
      this.setBeginDistance(0);
      this.setEndDistance(lineCount);
    }
  };

  public async navigateToPosition(
    input: number | AlignmentPosition,
    quaternion?: Quaternion
  ): Promise<void> {
    if (this.status() !== AlignmentNavigationStatus.Navigating) {
      return;
    }

    // Normalize input to AlignmentPosition
    const pos: AlignmentPosition =
      typeof input === 'number'
        ? { chainage: input, isInOverLength: false }
        : input;

    const navPosModelId =
      getTrbModelId(this.alignmentNavigationTool.alignmentModelId) ??
      this.alignmentNavigationTool.alignmentModelId;
    const metadata = this.trajectoryMetadataProvider.getById(navPosModelId);

    if (!metadata?.isAlignment) {
      const targetChainage = pos.chainage ?? pos.distance;
      if (targetChainage !== undefined) {
        this.alignmentNavigationTool.navigateToChainage(
          this.alignmentNavigationTool.alignmentEntityId!,
          targetChainage,
          quaternion
        );
      }
      return;
    }

    let chainage = pos.chainage;
    let isInOverLength = pos.isInOverLength ?? false;

    // If distance is provided, convert to chainage first
    if (pos.distance !== undefined && metadata.sectionChainages) {
      const section = metadata.sectionChainages.find(
        (s) =>
          pos.distance! >= s.beginDistance && pos.distance! <= s.endDistance
      );
      if (section) {
        const localDist = pos.distance - section.beginDistance;
        const result = cumDistanceToChainage(localDist, section);
        if (result) {
          chainage = result.chainage;
          isInOverLength = result.isInOverLength;
        }
      }
    }

    if (chainage === undefined) return;

    const sectionChainageData = metadata.sectionChainages?.find(
      (section: SectionChainageData) =>
        chainage! >= section.beginStation && chainage! <= section.endStation
    );
    if (!sectionChainageData) return;

    const web3dChainage = chainageToWeb3dChainage(
      { chainage, isInOverLength },
      sectionChainageData
    );
    if (web3dChainage !== undefined) {
      this.alignmentNavigationTool.navigateToChainage(
        sectionChainageData.entityId,
        web3dChainage,
        quaternion
      );
    }
  }
}
